import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";

import {
  ALLOWED_ACCEPT,
  fetchFiles,
  fileDownloadUrl,
  formatFileSize,
  replaceFile,
  uploadFile,
  validateFile,
  versionDownloadUrl,
  type FileItem,
  type FileOwner,
} from "../lib/files";
import { HelpLink } from "../help/links";
import { formatDateTime } from "../lib/format";

/**
 * Панель файлов договора/спецификации (E6). Загрузка/замена сканов — право `contract:register`
 * (проп `canUpload`; сервер авторитетен, матрица 4.3, правило 5). Скачивание — всем ролям (ссылка).
 * Файл НЕ удаляется, только ЗАМЕНЯЕТСЯ новой версией; прежние версии видны как история и доступны
 * на скачивание. Оформление — токены docs/07 (правило 9); имена/типы — из allowlist PDF/JPG/PNG/DOCX.
 */

const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const linkCls = "text-accent hover:underline";

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly files: FileItem[] }
  | { readonly kind: "error"; readonly message: string };

export interface FilesPanelProps {
  readonly owner: FileOwner;
  /** Может ли текущая роль загружать/заменять сканы (право `contract:register`). */
  readonly canUpload: boolean;
  /** Заголовок панели (по умолчанию «Файлы»). Для спецификаций можно передать свой. */
  readonly title?: string;
}

export function FilesPanel({ owner, canUpload, title = "Файлы" }: FilesPanelProps) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  /**
   * Замечание об отказе и ЕГО ПРИРОДА. «Подробнее» ставится только у ДОМЕННОГО отказа — типа и
   * размера файла: это правило продукта, и статья на него отвечает. Ответ сервера может оказаться
   * и общим отказом в правах, а его в E16 не трогаем (граница плана §3).
   */
  const [notice, setNotice] = useState<{ text: string; domain: boolean } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const uploadRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const replaceTargetId = useRef<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchFiles(owner);
    if (res.ok) setState({ kind: "ready", files: res.value });
    else setState({ kind: "error", message: res.message });
  }, [owner]);

  useEffect(() => {
    void load();
  }, [load]);

  const runUpload = useCallback(
    async (file: File, action: () => Promise<{ ok: boolean; message?: string }>) => {
      const check = validateFile(file);
      if (!check.ok) {
        setNotice({ text: check.message, domain: true });
        return;
      }
      setNotice(null);
      setBusy(true);
      const res = await action();
      setBusy(false);
      if (res.ok) await load();
      else setNotice({ text: res.message ?? "Не удалось загрузить файл.", domain: false });
    },
    [load],
  );

  const onPickUpload = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // позволяем выбрать тот же файл повторно
      if (file) void runUpload(file, () => uploadFile(owner, file));
    },
    [owner, runUpload],
  );

  const onPickReplace = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const targetId = replaceTargetId.current;
      e.target.value = "";
      if (file && targetId) void runUpload(file, () => replaceFile(targetId, file));
    },
    [runUpload],
  );

  const startReplace = useCallback((fileId: string) => {
    replaceTargetId.current = fileId;
    replaceRef.current?.click();
  }, []);

  const toggleHistory = useCallback((fileId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[15px] font-bold text-ink">
          {title}
          {state.kind === "ready" && state.files.length > 0 ? ` (${state.files.length})` : ""}
        </h3>
        {canUpload && (
          <>
            <button
              type="button"
              className={btnSecondary}
              disabled={busy}
              onClick={() => uploadRef.current?.click()}
            >
              {busy ? "Загрузка…" : "Загрузить файл"}
            </button>
            <input
              ref={uploadRef}
              type="file"
              accept={ALLOWED_ACCEPT}
              aria-label="Выберите файл для загрузки"
              className="hidden"
              onChange={onPickUpload}
            />
            <input
              ref={replaceRef}
              type="file"
              accept={ALLOWED_ACCEPT}
              aria-label="Выберите файл для замены"
              className="hidden"
              onChange={onPickReplace}
            />
          </>
        )}
      </div>

      {canUpload && (
        <p className="mb-2 text-[12px] text-muted">Разрешены PDF, JPG, PNG, DOCX. До 50 МБ.</p>
      )}

      {notice && (
        <p role="alert" className="mb-2 text-[13px] text-overdue-fg">
          {notice.text} {notice.domain && <HelpLink topic="fileUploadRejected" />}
        </p>
      )}

      {state.kind === "loading" && (
        <p className="text-[13px] text-muted" role="status">
          Загрузка файлов…
        </p>
      )}

      {state.kind === "error" && (
        <div role="alert" className="text-[13px]">
          <p className="text-overdue-fg">{state.message}</p>
          <button type="button" onClick={() => void load()} className={`mt-2 ${btnSecondary}`}>
            Повторить
          </button>
        </div>
      )}

      {state.kind === "ready" && state.files.length === 0 && (
        <p className="text-[13px] text-muted">Файлов пока нет.</p>
      )}

      {state.kind === "ready" && state.files.length > 0 && (
        <ul className="divide-y divide-border">
          {state.files.map((f) => (
            <li key={f.id} className="py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <a href={fileDownloadUrl(f.id)} className={linkCls}>
                    {f.current.originalName}
                  </a>
                  <div className="text-[12px] text-muted">
                    {formatFileSize(f.current.size)} · версия {f.current.version} ·{" "}
                    {formatDateTime(f.current.createdAt)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {f.versions.length > 1 && (
                    <button
                      type="button"
                      className="text-[13px] text-muted hover:text-ink"
                      aria-expanded={expanded.has(f.id)}
                      onClick={() => toggleHistory(f.id)}
                    >
                      История ({f.versions.length})
                    </button>
                  )}
                  {canUpload && (
                    <button
                      type="button"
                      className={btnSecondary}
                      disabled={busy}
                      onClick={() => startReplace(f.id)}
                    >
                      Заменить
                    </button>
                  )}
                </div>
              </div>

              {expanded.has(f.id) && (
                <ul className="mt-2 ml-3 border-l border-border pl-3 text-[12px] text-muted">
                  {f.versions.map((v) => (
                    <li key={v.id} className="py-0.5">
                      <a href={versionDownloadUrl(f.id, v.id)} className={linkCls}>
                        версия {v.version}
                      </a>{" "}
                      · {v.originalName} · {formatFileSize(v.size)} · {formatDateTime(v.createdAt)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
